import React, { createContext, useContext, useState, useEffect } from 'react';

interface AuthContextType {
  isAuthenticated: boolean;
  username: string | null;
  login: (username: string, password: string) => boolean;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: React.ReactNode;
}

// Simple credentials - in a real app these would be environment variables or from a secure API
const VALID_CREDENTIALS = {
  'spring': 'thespringai',
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [username, setUsername] = useState<string | null>(null);

  // Check if user is already logged in on app start
  useEffect(() => {
    const savedAuth = localStorage.getItem('spring-auth');
    if (savedAuth) {
      try {
        const { username: savedUsername, timestamp } = JSON.parse(savedAuth);
        // Session expires after 24 hours
        const isExpired = Date.now() - timestamp > 24 * 60 * 60 * 1000;
        
        if (!isExpired && savedUsername) {
          setIsAuthenticated(true);
          setUsername(savedUsername);
        } else {
          localStorage.removeItem('spring-auth');
        }
      } catch (error) {
        localStorage.removeItem('spring-auth');
      }
    }
  }, []);

  const login = (inputUsername: string, inputPassword: string): boolean => {
    const validPassword = VALID_CREDENTIALS[inputUsername as keyof typeof VALID_CREDENTIALS];
    
    if (validPassword && validPassword === inputPassword) {
      setIsAuthenticated(true);
      setUsername(inputUsername);
      
      // Save to localStorage with timestamp
      localStorage.setItem('spring-auth', JSON.stringify({
        username: inputUsername,
        timestamp: Date.now()
      }));
      
      return true;
    }
    
    return false;
  };

  const logout = () => {
    setIsAuthenticated(false);
    setUsername(null);
    localStorage.removeItem('spring-auth');
  };

  const contextValue: AuthContextType = {
    isAuthenticated,
    username,
    login,
    logout
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 